import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * App-wide live data + auto refresh.
 * - visitors: the same counter shown in the header and on the home page (counted once per visit, then polled)
 * - site: contact details / social links / intro video edited in Admin → CMS → Site settings
 * - tick: increases every REFRESH_MS and whenever the tab becomes visible again; pages add it to their
 *         data-loading effects so everything refreshes on its own
 * - new deploys are detected and the page reloads itself at a safe moment (route change / tab refocus)
 */
const LiveContext = createContext({ visitors: null, site: {}, tick: 0, refresh: () => {} });
const REFRESH_MS = 45000;
const VISITORS_MS = 8000; // header + home counters tick live

/** Shown until (and whenever) the server has nothing saved, so contact details are never blank. */
export const SITE_DEFAULTS = {
  phone: "+91 74112 98777",
  whatsapp: "917411298777",
  email: "hello@dartmyvenue.com",
  address: "Belgaum, Karnataka, India",
  hours: "9 AM – 9 PM, every day",
  intro_video_url: "", instagram: "", facebook: "", youtube: "",
};
const mergeSite = (d) => {
  const out = { ...SITE_DEFAULTS };
  Object.entries(d || {}).forEach(([k, v]) => { if (v !== null && v !== undefined && String(v).trim() !== "") out[k] = v; });
  return out;
};
const VERSION_MS = 60000;

function currentBundle() {
  const s = Array.from(document.scripts).find((x) => /\/static\/js\/main\.[\w]+\.js/.test(x.src));
  return s ? new URL(s.src).pathname : null;
}

export function LiveProvider({ children }) {
  const [visitors, setVisitors] = useState(null);
  const [site, setSite] = useState(SITE_DEFAULTS);
  const [tick, setTick] = useState(0);
  const updateReady = useRef(false);
  const counted = useRef(false);
  const loc = useLocation();

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // count this visit once, then keep the number live
  useEffect(() => {
    if (counted.current) return;
    counted.current = true;
    api.post("/visitors/increment").then((r) => setVisitors(r.data?.count ?? null)).catch(() => {
      api.get("/visitors/count").then((r) => setVisitors(r.data?.count ?? null)).catch(() => {});
    });
  }, []);

  useEffect(() => {
    api.get("/config/site").then((r) => setSite(mergeSite(r.data))).catch(() => {});
  }, [tick]);

  // live visitor number: polled every few seconds while the tab is visible
  useEffect(() => {
    let alive = true;
    const pull = () => {
      if (document.hidden) return;
      api.get("/visitors/count").then((r) => { if (alive && typeof r.data?.count === "number") setVisitors((v) => (v === null || r.data.count >= v ? r.data.count : v)); }).catch(() => {});
    };
    const t = setInterval(pull, VISITORS_MS);
    const onVis = () => { if (!document.hidden) pull(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // periodic + on-focus refresh
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) refresh(); }, REFRESH_MS);
    const onVis = () => {
      if (document.hidden) return;
      if (updateReady.current) { window.location.reload(); return; }
      refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    window.addEventListener("online", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); window.removeEventListener("online", onVis); };
  }, [refresh]);

  // new version detection (production builds only)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    const mine = currentBundle();
    if (!mine) return;
    const check = async () => {
      try {
        const r = await fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const m = await r.json();
        const latest = m?.files?.["main.js"];
        if (latest && latest !== mine) updateReady.current = true;
      } catch {}
    };
    const t = setInterval(check, VERSION_MS);
    check();
    return () => clearInterval(t);
  }, []);

  // reload into the new version on the next page change
  useEffect(() => {
    if (updateReady.current) window.location.reload();
  }, [loc.pathname]);

  return <LiveContext.Provider value={{ visitors, site, tick, refresh }}>{children}</LiveContext.Provider>;
}

export const useLive = () => useContext(LiveContext);
export const useTick = () => useContext(LiveContext).tick;
